import { TrashIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FormErrors, UpdateProfileUIProps, YoutubeItem } from "../../../types/updateProfileProps";
import { AddPill, Card, FormActions, SectionTitle, TextField, isHttpUrl } from "./formPrimitives";

/** Backend contract: YtLinkDetailsDto – shortTitle max 25, shortDescription max 200 */
const URL_MAX_LENGTH = 5000;
const TITLE_MAX_LENGTH = 25;
const DESCRIPTION_MAX_LENGTH = 200;

const EMPTY_DRAFT: YoutubeItem = { ytLink: "", shortTitle: "", shortDescription: "" };

const YoutubeSection: React.FC<UpdateProfileUIProps> = ({ ytLinks, commitYtLinks, isPersisting }) => {
    const { t } = useTranslation("updateProfile");

    const [isAdding, setIsAdding] = useState(false);
    const [draft, setDraft] = useState<YoutubeItem>(EMPTY_DRAFT);
    const [errors, setErrors] = useState<FormErrors>({});

    const updateDraft = (key: keyof YoutubeItem, value: string) => {
        setDraft((previous) => ({ ...previous, [key]: value }));
        setErrors((previous) => ({ ...previous, [key]: "" }));
    };

    const closeForm = () => {
        setIsAdding(false);
        setDraft(EMPTY_DRAFT);
        setErrors({});
    };

    const validate = (item: YoutubeItem): FormErrors => {
        const formErrors: FormErrors = {};
        const requiredMessage = t("page.errors.required");

        if (!item.ytLink) {
            formErrors.ytLink = requiredMessage;
        } else if (!isHttpUrl(item.ytLink)) {
            formErrors.ytLink = t("page.errors.invalidUrl");
        } else if (item.ytLink.length > URL_MAX_LENGTH) {
            formErrors.ytLink = t("page.errors.maxLength", { max: URL_MAX_LENGTH });
        }

        if (!item.shortTitle) formErrors.shortTitle = requiredMessage;
        if (!item.shortDescription) formErrors.shortDescription = requiredMessage;

        return formErrors;
    };

    const addLink = async () => {
        const item: YoutubeItem = {
            ytLink: draft.ytLink.trim(),
            shortTitle: draft.shortTitle.trim(),
            shortDescription: draft.shortDescription.trim(),
        };

        const formErrors = validate(item);
        setErrors(formErrors);
        if (Object.keys(formErrors).length > 0) return;

        const isSaved = await commitYtLinks([...ytLinks, item]);
        if (isSaved) closeForm();
    };

    const removeLink = (index: number) => {
        commitYtLinks(ytLinks.filter((_, itemIndex) => itemIndex !== index));
    };

    return (
        <section id="section-youtube-links" className="flex flex-col gap-4">
            <SectionTitle>{t("page.youtubeLinks")}</SectionTitle>

            {!isAdding && (
                <AddPill onClick={() => setIsAdding(true)} disabled={isPersisting}>
                    {t("page.addYoutubeLinks")}
                </AddPill>
            )}

            {ytLinks.map((item, index) => (
                <Card key={`${index}-${item.ytLink}`} className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-col gap-1">
                        <p className="break-words text-base font-bold leading-6 text-gray-900">{item.shortTitle}</p>
                        <p className="break-words text-sm leading-5 text-gray-500">{item.shortDescription}</p>
                        {isHttpUrl(item.ytLink) ? (
                            <a
                                href={item.ytLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="break-all text-sm text-[#0485F7] hover:underline"
                            >
                                {item.ytLink}
                            </a>
                        ) : (
                            <span className="break-all text-sm text-gray-500">{item.ytLink}</span>
                        )}
                    </div>
                    <button
                        type="button"
                        aria-label={t("page.remove")}
                        disabled={isPersisting}
                        onClick={() => removeLink(index)}
                        className="mt-1 shrink-0 text-gray-900 transition-colors hover:text-red-600 disabled:opacity-50"
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </Card>
            ))}

            {isAdding && (
                <Card className="flex flex-col gap-3">
                    <TextField
                        name="youtubeUrl"
                        label={t("page.youtubeUrl")}
                        value={draft.ytLink}
                        maxLength={URL_MAX_LENGTH}
                        placeholder={t("page.enterUrl")}
                        onValueChange={(value) => updateDraft("ytLink", value)}
                        error={errors.ytLink}
                    />
                    <TextField
                        name="youtubeTitle"
                        label={t("page.shortTitle")}
                        value={draft.shortTitle}
                        maxLength={TITLE_MAX_LENGTH}
                        placeholder={t("page.enterShortTitle")}
                        hint={t("page.characters", { count: draft.shortTitle.length, max: TITLE_MAX_LENGTH })}
                        onValueChange={(value) => updateDraft("shortTitle", value)}
                        error={errors.shortTitle}
                    />
                    <TextField
                        name="youtubeDescription"
                        label={t("page.shortDescription")}
                        value={draft.shortDescription}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        placeholder={t("page.enterShortDescription")}
                        hint={t("page.characters", {
                            count: draft.shortDescription.length,
                            max: DESCRIPTION_MAX_LENGTH,
                        })}
                        onValueChange={(value) => updateDraft("shortDescription", value)}
                        error={errors.shortDescription}
                    />
                    <FormActions
                        cancelLabel={t("page.cancel")}
                        confirmLabel={t("page.add")}
                        busy={isPersisting}
                        onCancel={closeForm}
                        onConfirm={addLink}
                    />
                </Card>
            )}
        </section>
    );
};

export default YoutubeSection;
